import { Trans, useLingui } from "@lingui/react/macro";

import { SECTION_LABELS } from "../../model/SECTION_LABELS";
import { SITE_EVENT_LABELS } from "../../model/SITE_EVENT_LABELS";
import type { DashboardEventVisitors, DashboardSectionVisitors } from "../../model/TeamDashboard";
import { BarList } from "./BarList";
import { DashboardCard } from "./DashboardCard";

type SectionsProps = {
  sections: DashboardSectionVisitors[];
  events: DashboardEventVisitors[];
};

// «Что открывают и делают»: уникальные посетители каждого раздела по Umami, по убыванию; ниже —
// действия на страницах (события Umami, `trackEvent`). Без Umami — подсказка вместо разделов.
export const Sections = ({ sections, events }: SectionsProps) => {
  const { i18n } = useLingui();
  const number = new Intl.NumberFormat(i18n.locale);
  const sorted = [...sections].sort((a, b) => b.visitors - a.visitors);
  const most = Math.max(1, ...sorted.map((row) => row.visitors));
  const actions = [...events].sort((a, b) => b.visitors - a.visitors);

  return (
    <DashboardCard
      title={<Trans>What they open and do</Trans>}
      description={<Trans>Visitors who opened a section at least once, Umami</Trans>}
    >
      {sorted.length === 0 ? (
        <p className="text-[13px] text-muted">
          <Trans>Umami is not connected yet — no section data.</Trans>
        </p>
      ) : (
        <ul className="flex flex-col gap-2.5">
          {sorted.map((row) => (
            <li
              key={row.section}
              data-section={row.section}
              className="grid grid-cols-[128px_minmax(0,1fr)_52px] items-center gap-2.5 text-[13px]"
            >
              <span className="text-foreground-soft">{i18n._(SECTION_LABELS[row.section])}</span>
              <span className="flex h-3.5 items-center">
                <span
                  className="h-3 min-w-1 rounded-r bg-chart-amber"
                  style={{ width: `${String((row.visitors / most) * 100)}%` }}
                />
              </span>
              <span className="text-right font-mono text-sm font-semibold tabular-nums">
                {number.format(row.visitors)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {actions.length > 0 && (
        <div className="flex flex-col gap-2.5 border-t border-border/60 pt-3">
          <span className="text-xs text-subtle">
            <Trans>Actions, Umami visitors</Trans>
          </span>
          <BarList
            data-list="actions"
            labelWidth="wide"
            rows={actions.map((row) => ({
              key: row.event,
              label: i18n._(SITE_EVENT_LABELS[row.event]),
              value: row.visitors,
            }))}
          />
        </div>
      )}
    </DashboardCard>
  );
};
